/**
 * WizardChoiceDialog Component
 * 
 * Shows when multiple wizards could auto-launch
 * Lets user choose which wizard to use or skip all
 */

import React from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Button } from './ui/button';
import { Card, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Badge } from './ui/badge';
import { Sparkles, X } from 'lucide-react';
import { formatCategoryLabel } from '../utils/normalizeAcronyms';

interface WizardOption {
  id: string;
  name: string;
  description: string;
  category: string;
}

interface WizardChoiceDialogProps {
  open: boolean;
  onClose: () => void;
  wizards: WizardOption[];
  onSelectWizard: (wizardId: string) => void;
  configItemName?: string;
}

export function WizardChoiceDialog({
  open,
  onClose,
  wizards,
  onSelectWizard,
  configItemName
}: WizardChoiceDialogProps) {
  // When exactly one wizard applies, auto-select it instead of showing a chooser.
  // The hook must run on every render (rules-of-hooks), so the condition lives inside it.
  const singleWizardId = wizards.length === 1 ? wizards[0].id : null;
  React.useEffect(() => {
    if (open && singleWizardId) {
      onSelectWizard(singleWizardId);
    }
    // onSelectWizard is intentionally omitted: fire once per open, not per parent render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, singleWizardId]);

  if (wizards.length <= 1) return null;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-primary" />
            Setup Wizard Available
          </DialogTitle>
          <DialogDescription>
            {configItemName 
              ? `We found ${wizards.length} wizards that can help you set up "${configItemName}"`
              : `We found ${wizards.length} wizards that can help you get started`
            }
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3 mt-4">
          {wizards.map((wizard) => (
            <Card
              key={wizard.id}
              className="cursor-pointer hover:border-primary transition-colors"
              onClick={() => {
                onSelectWizard(wizard.id);
                onClose();
              }}
            >
              <CardHeader className="pb-3">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <CardTitle className="text-base flex items-center gap-2">
                      {wizard.name}
                      <Badge variant="outline" className="text-xs">
                        {formatCategoryLabel(wizard.category)}
                      </Badge>
                    </CardTitle>
                    <CardDescription className="mt-1.5">
                      {wizard.description}
                    </CardDescription>
                  </div>
                </div>
              </CardHeader>
            </Card>
          ))}
        </div>

        <div className="flex justify-between items-center mt-6 pt-4 border-t">
          <p className="text-sm text-muted-foreground">
            You can always access wizards later from the item actions menu
          </p>
          <Button variant="outline" onClick={onClose}>
            <X className="w-4 h-4 mr-2" />
            Skip for Now
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}